import React, { useState, useMemo, useEffect } from 'react';
import { X, Package, Check, ChevronRight, DollarSign, CreditCard, Gift, Wifi, AlertCircle } from 'lucide-react';
import { useProfile } from '../ProfileContext';
import CascadeDeletePrompt from './CascadeDeletePrompt';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

/**
 * BundleModal - Manage bundle subscriptions
 * 
 * Features:
 * - View bundle details and savings
 * - Switch to different bundle options
 * - Cancel bundle (remove all or keep individual services)
 * - Set payment method for retained individual services
 */
const BundleModal = ({ isOpen, onClose, bundleId, onCreditReallocationNeeded }) => {
  const {
    bundles,
    subscriptions,
    memberships,
    creditCards,
    ispInfo,
    refData,
    loadProfile,
  } = useProfile();

  // Local state
  const [selectedAction, setSelectedAction] = useState(null); // 'switch' | 'cancel' | 'individual'
  const [selectedNewBundle, setSelectedNewBundle] = useState(null);
  const [servicesToKeep, setServicesToKeep] = useState({});
  const [servicePaymentMethods, setServicePaymentMethods] = useState({});
  const [showConfirmPrompt, setShowConfirmPrompt] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Get current bundle data
  const currentBundle = useMemo(() => {
    return bundles?.find(b => b.id === bundleId);
  }, [bundles, bundleId]);

  // Get bundle reference data
  const bundleRef = useMemo(() => {
    if (!currentBundle) return null;
    return refData?.bundles?.find(b => b.bundle_code === currentBundle.bundle_code);
  }, [currentBundle, refData]);

  // Helper to get service names from bundle_services array
  const getServiceNames = (bundle) => {
    if (!bundle) return [];
    // bundle_services is an array of {service_name, included_tier, ...}
    if (bundle.bundle_services && Array.isArray(bundle.bundle_services)) {
      return bundle.bundle_services.map(bs => bs.service_name);
    }
    // Fallback for included_services if it exists
    if (bundle.included_services && Array.isArray(bundle.included_services)) {
      return bundle.included_services.map(s => typeof s === 'string' ? s : s.service_name);
    }
    return [];
  };

  // Helper to get retail price for a service at a specific tier
  const getServiceRetailPrice = (serviceName, tierName) => {
    const serviceRef = refData?.streamingServices?.find(s => s.service_name === serviceName);
    if (!serviceRef || !serviceRef.tiers) return 0;
    
    // Find matching tier
    const tier = serviceRef.tiers.find(t => t.tier_name === tierName);
    if (tier) return tier.monthly_cost || 0;
    
    // Fallback to first tier (usually cheapest)
    return serviceRef.tiers[0]?.monthly_cost || 0;
  };

  // Get services included in this bundle with proper pricing
  const includedServices = useMemo(() => {
    if (!bundleRef) return [];
    
    // Get bundle_services which includes tier info
    const bundleServices = bundleRef.bundle_services || [];
    
    return bundleServices.map(bs => {
      const sub = subscriptions.find(s => 
        s.service_name === bs.service_name && 
        s.payment_method === 'bundle' && 
        s.payment_source === currentBundle?.bundle_code
      );
      
      const retailPrice = getServiceRetailPrice(bs.service_name, bs.included_tier);
      
      return {
        name: bs.service_name,
        tier: bs.included_tier,
        subscription: sub,
        retailPrice,
      };
    });
  }, [bundleRef, subscriptions, currentBundle, refData]);

  // Calculate bundle savings
  const bundleSavings = useMemo(() => {
    const retailTotal = includedServices.reduce((sum, s) => sum + (s.retailPrice || 0), 0);
    const bundlePrice = currentBundle?.monthly_cost || bundleRef?.bundle_price || 0;
    return {
      retailTotal,
      bundlePrice,
      savings: retailTotal - bundlePrice,
      savingsPercent: retailTotal > 0 ? Math.round(((retailTotal - bundlePrice) / retailTotal) * 100) : 0,
    };
  }, [includedServices, currentBundle, bundleRef]);

  // Get alternative bundle options (same provider, different configurations)
  const alternativeBundles = useMemo(() => {
    if (!bundleRef) return [];
    
    // Find bundles that share at least one service with current bundle
    const currentServiceNames = getServiceNames(bundleRef);
    const currentServices = new Set(currentServiceNames);
    
    return (refData?.bundles || [])
      .filter(b => {
        if (b.bundle_code === currentBundle?.bundle_code) return false;
        const bundleServiceNames = getServiceNames(b);
        return bundleServiceNames.some(s => currentServices.has(s));
      })
      .map(b => {
        const bundleServices = b.bundle_services || [];
        const retailTotal = bundleServices.reduce((sum, bs) => {
          return sum + getServiceRetailPrice(bs.service_name, bs.included_tier);
        }, 0);
        
        const bundlePrice = b.monthly_cost || 0;
        
        return {
          ...b,
          serviceNames: bundleServices.map(bs => bs.service_name),
          retailTotal,
          savings: retailTotal - bundlePrice,
        };
      })
      .sort((a, b) => (a.monthly_cost || 0) - (b.monthly_cost || 0));
  }, [bundleRef, currentBundle, refData]);

  // Get available payment methods for individual services
  const availablePaymentMethods = useMemo(() => {
    const methods = [
      { value: 'paid', label: 'Pay directly', icon: DollarSign },
    ];

    // Add credit card options
    creditCards?.forEach(card => {
      if (card.streaming_credit_monthly > 0) {
        methods.push({
          value: `card_${card.id}`,
          label: `${card.card_name} credit`,
          icon: CreditCard,
          cardId: card.id,
          cardName: card.card_name,
        });
      }
    });

    // Add membership options
    memberships?.forEach(m => {
      const membershipPerks = refData?.membershipPerks?.filter(p => p.membership_type === m.membership_type) || [];
      membershipPerks.forEach(perk => {
        methods.push({
          value: `membership_${m.id}_${perk.service_name}`,
          label: `Free via ${m.membership_name || m.membership_type}`,
          icon: Gift,
          membershipId: m.id,
          membershipType: m.membership_type,
          serviceName: perk.service_name,
        });
      });
    });

    // Add ISP options
    if (ispInfo?.provider) {
      const ispPerks = refData?.ispPerks?.filter(p => p.isp_name === ispInfo.provider) || [];
      ispPerks.forEach(perk => {
        methods.push({
          value: `isp_${perk.service_name}`,
          label: `Free via ${ispInfo.provider}`,
          icon: Wifi,
          serviceName: perk.service_name,
        });
      });
    }

    return methods;
  }, [creditCards, memberships, ispInfo, refData]);

  // Detect individual subscriptions that will be consolidated when switching to selected bundle
  const switchConflicts = useMemo(() => {
    if (!selectedNewBundle) return [];
    
    const newBundleRef = refData?.bundles?.find(b => b.bundle_code === selectedNewBundle);
    if (!newBundleRef) return [];
    
    const newBundleServices = newBundleRef.bundle_services || [];
    const newServiceNames = newBundleServices.map(bs => bs.service_name.toLowerCase());
    
    // Current bundle services (already in a bundle, not conflicts)
    const currentBundleServiceNames = includedServices.map(s => s.name.toLowerCase());
    
    // Find individual subscriptions that will be replaced
    return subscriptions.filter(sub => {
      // Must be an individual subscription (not bundle, not perk, not free)
      if (sub.payment_method === 'bundle' || sub.payment_method === 'perk' || sub.payment_method === 'free') {
        return false;
      }
      // Must be in the new bundle
      if (!newServiceNames.includes(sub.service_name.toLowerCase())) {
        return false;
      }
      // Must not already be in current bundle
      if (currentBundleServiceNames.includes(sub.service_name.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [selectedNewBundle, refData, subscriptions, includedServices]);

  // Initialize service selection when modal opens
  useEffect(() => {
    if (isOpen && includedServices.length > 0) {
      const initialKeep = {};
      const initialPayment = {};
      includedServices.forEach(s => {
        initialKeep[s.name] = true;
        initialPayment[s.name] = 'paid';
      });
      setServicesToKeep(initialKeep);
      setServicePaymentMethods(initialPayment);
      setSelectedAction(null);
      setSelectedNewBundle(null);
    }
  }, [isOpen, includedServices]);

  // Get filtered payment methods for a specific service
  const getPaymentMethodsForService = (serviceName) => {
    return availablePaymentMethods.filter(m => {
      // 'paid' is always available
      if (m.value === 'paid') return true;
      // Card credit is always available
      if (m.value.startsWith('card_')) return true;
      // Membership/ISP perks only for matching service
      if (m.serviceName && m.serviceName !== serviceName) return false;
      return true;
    });
  };

  // Handle confirm action
  const handleConfirm = async () => {
    setIsProcessing(true);
    
    try {
      const userId = currentBundle?.user_id;
      if (!userId) throw new Error('User ID not found');

      if (selectedAction === 'cancel') {
        // Cancel bundle entirely - remove bundle and all services
        await supabase.from('credit_card_allocations').delete().eq('bundle_id', bundleId);
        
        for (const service of includedServices) {
          if (service.subscription?.id) {
            await supabase.from('subscriptions').delete().eq('id', service.subscription.id);
          }
        }
        
        await supabase.from('user_bundles').delete().eq('id', bundleId);
        
        toast.success('Bundle cancelled successfully');
        
      } else if (selectedAction === 'switch' && selectedNewBundle) {
        // Switch to different bundle
        const newBundleRef = refData?.bundles?.find(b => b.bundle_code === selectedNewBundle);
        if (!newBundleRef) throw new Error('New bundle not found');

        const newMonthlyPrice = newBundleRef.monthly_cost || 0;
        const currentCreditApplied = parseFloat(currentBundle.credit_applied) || 0;
        
        // If new bundle costs more than current credit, cap the credit
        // If new bundle costs less, keep the credit (up to the new price)
        const newCreditApplied = Math.min(currentCreditApplied, newMonthlyPrice);
        const newActualCost = Math.max(0, newMonthlyPrice - newCreditApplied);
        
        // Check if credit reallocation is needed
        const needsCreditReallocation = newMonthlyPrice > currentCreditApplied;

        // Update bundle record with proper actual_cost
        await supabase.from('user_bundles')
          .update({
            bundle_code: selectedNewBundle,
            monthly_cost: newMonthlyPrice,
            credit_applied: newCreditApplied,
            actual_cost: newActualCost,
          })
          .eq('id', bundleId);

        // Determine services to add/remove
        const currentServiceNamesList = includedServices.map(s => s.name);
        const currentServiceNamesSet = new Set(currentServiceNamesList);
        const newBundleServices = newBundleRef.bundle_services || [];
        const newServiceNamesSet = new Set(newBundleServices.map(bs => bs.service_name));

        // Remove services no longer in bundle
        for (const service of includedServices) {
          if (!newServiceNamesSet.has(service.name) && service.subscription?.id) {
            await supabase.from('subscriptions').delete().eq('id', service.subscription.id);
          }
        }

        // Add new services with proper tier pricing
        // But first check if user has these as individual subscriptions
        for (const bs of newBundleServices) {
          if (!currentServiceNamesSet.has(bs.service_name)) {
            // Check if user has this service as an individual (non-bundle) subscription
            const existingIndividualSub = subscriptions.find(s => 
              s.service_name.toLowerCase() === bs.service_name.toLowerCase() &&
              s.payment_method !== 'bundle'
            );
            
            if (existingIndividualSub) {
              // User has this individually - delete it since bundle will cover it
              await supabase.from('credit_card_allocations').delete().eq('subscription_id', existingIndividualSub.id);
              await supabase.from('subscriptions').delete().eq('id', existingIndividualSub.id);
            }
            
            const retailPrice = getServiceRetailPrice(bs.service_name, bs.included_tier);
            await supabase.from('subscriptions').insert({
              user_id: userId,
              service_name: bs.service_name,
              subscription_tier: bs.included_tier || 'Standard',
              monthly_cost: 0,
              retail_cost: retailPrice,
              payment_method: 'bundle',
              payment_source: selectedNewBundle,
              actual_cost: 0,
            });
          }
        }

        // Update existing services to point to new bundle and update tier/retail_cost if needed
        for (const service of includedServices) {
          if (newServiceNamesSet.has(service.name) && service.subscription?.id) {
            const newBs = newBundleServices.find(bs => bs.service_name === service.name);
            const newRetailPrice = newBs ? getServiceRetailPrice(newBs.service_name, newBs.included_tier) : service.retailPrice;
            await supabase.from('subscriptions')
              .update({ 
                payment_source: selectedNewBundle,
                subscription_tier: newBs?.included_tier || service.tier,
                retail_cost: newRetailPrice,
              })
              .eq('id', service.subscription.id);
          }
        }

        if (needsCreditReallocation) {
          toast.success(`Switched to ${newBundleRef.bundle_name}. The new bundle costs more than your current credit allocation - please update your credit card allocations.`, {
            duration: 6000,
          });
        } else {
          toast.success(`Switched to ${newBundleRef.bundle_name}`);
        }

      } else if (selectedAction === 'individual') {
        // Convert to individual services
        
        // First, delete bundle and its allocations
        await supabase.from('credit_card_allocations').delete().eq('bundle_id', bundleId);
        await supabase.from('user_bundles').delete().eq('id', bundleId);

        // Process each service
        for (const service of includedServices) {
          const keep = servicesToKeep[service.name];
          const paymentMethod = servicePaymentMethods[service.name];
          
          if (!keep) {
            // Delete service
            if (service.subscription?.id) {
              await supabase.from('subscriptions').delete().eq('id', service.subscription.id);
            }
          } else {
            // Update service with new payment method
            const serviceRef = refData?.streamingServices?.find(s => s.service_name === service.name);
            let updateData = {
              payment_method: 'paid',
              payment_source: null,
              monthly_cost: serviceRef?.base_price || service.retailPrice || 0,
              actual_cost: serviceRef?.base_price || service.retailPrice || 0,
              credit_applied: 0,
            };

            if (paymentMethod.startsWith('card_')) {
              const cardId = paymentMethod.replace('card_', '');
              const card = creditCards?.find(c => c.id === cardId);
              updateData.payment_method = 'credit_card_perk';
              updateData.payment_source = card?.card_name;
              // Note: Credit allocation should be done separately in credit card section
            } else if (paymentMethod.startsWith('membership_')) {
              const parts = paymentMethod.split('_');
              const membershipId = parts[1];
              const membership = memberships?.find(m => m.id === membershipId);
              updateData.payment_method = 'membership_benefit';
              updateData.payment_source = membership?.membership_type;
              updateData.monthly_cost = 0;
              updateData.actual_cost = 0;
            } else if (paymentMethod.startsWith('isp_')) {
              updateData.payment_method = 'isp_bundle';
              updateData.payment_source = ispInfo?.provider;
              updateData.monthly_cost = 0;
              updateData.actual_cost = 0;
            }

            if (service.subscription?.id) {
              await supabase.from('subscriptions')
                .update(updateData)
                .eq('id', service.subscription.id);
            }
          }
        }

        toast.success('Converted to individual services');
      }

      // Reload profile to refresh all data
      await loadProfile();
      
      setShowConfirmPrompt(false);
      onClose();
      
      // Trigger credit reallocation prompt if needed (after modal closes)
      if (selectedAction === 'switch' || selectedAction === 'individual') {
        if (onCreditReallocationNeeded) {
          // Small delay to let modal close first
          setTimeout(() => onCreditReallocationNeeded(), 300);
        }
      }
      
    } catch (error) {
      console.error('Error processing bundle change:', error);
      toast.error('Failed to process changes');
    } finally {
      setIsProcessing(false);
    }
  };

  // Build confirmation prompt content
  const getConfirmationContent = () => {
    if (selectedAction === 'cancel') {
      return {
        title: 'Cancel Bundle?',
        message: 'This will remove the bundle and all included services.',
        affectedItems: includedServices.map(s => ({ name: s.name, description: 'will be removed' })),
        actions: [
          { label: 'Cancel Bundle', onClick: handleConfirm, variant: 'danger' },
          { label: 'Go Back', onClick: () => setShowConfirmPrompt(false), variant: 'secondary' },
        ],
      };
    }
    
    if (selectedAction === 'switch') {
      const newBundle = refData?.bundles?.find(b => b.bundle_code === selectedNewBundle);
      const currentServices = new Set(includedServices.map(s => s.name));
      const newServiceNames = getServiceNames(newBundle);
      const newServices = new Set(newServiceNames);
      
      const removing = includedServices.filter(s => !newServices.has(s.name));
      const adding = newServiceNames.filter(s => !currentServices.has(s));
      
      const items = [];
      removing.forEach(s => items.push({ name: s.name, description: 'will be removed' }));
      adding.forEach(s => items.push({ name: s, description: 'will be added' }));
      
      return {
        title: `Switch to ${newBundle?.bundle_name}?`,
        message: `Your monthly cost will change from $${bundleSavings.bundlePrice.toFixed(2)} to $${newBundle?.monthly_cost?.toFixed(2) || '0.00'}.`,
        affectedItems: items.length > 0 ? items : [{ name: 'No service changes', description: '' }],
        actions: [
          { label: 'Switch Bundle', onClick: handleConfirm, variant: 'primary' },
          { label: 'Go Back', onClick: () => setShowConfirmPrompt(false), variant: 'secondary' },
        ],
      };
    }
    
    if (selectedAction === 'individual') {
      const keeping = includedServices.filter(s => servicesToKeep[s.name]);
      const removing = includedServices.filter(s => !servicesToKeep[s.name]);
      
      const items = [];
      keeping.forEach(s => {
        const method = servicePaymentMethods[s.name];
        const methodLabel = availablePaymentMethods.find(m => m.value === method)?.label || 'Pay directly';
        items.push({ name: s.name, description: methodLabel });
      });
      removing.forEach(s => items.push({ name: s.name, description: 'will be removed' }));
      
      return {
        title: 'Convert to Individual Services?',
        message: 'The bundle will be cancelled and selected services will be kept with the chosen payment methods.',
        affectedItems: items,
        actions: [
          { label: 'Confirm Changes', onClick: handleConfirm, variant: 'primary' },
          { label: 'Go Back', onClick: () => setShowConfirmPrompt(false), variant: 'secondary' },
        ],
      };
    }
    
    return { title: '', message: '', affectedItems: [], actions: [] };
  };

  if (!isOpen || !currentBundle) return null;

  const confirmContent = getConfirmationContent();

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-600 to-blue-600">
            <div className="flex items-center gap-3 text-white">
              <Package className="w-6 h-6" />
              <div>
                <h2 className="text-lg font-semibold">{bundleRef?.bundle_name || currentBundle.bundle_code}</h2>
                <p className="text-sm text-purple-100">Manage your bundle</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            
            {/* Current Bundle Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">Included Services</h3>
              <div className="space-y-2">
                {includedServices.map(service => (
                  <div key={service.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{service.name}</span>
                    </div>
                    <span className="text-gray-500">${service.retailPrice?.toFixed(2)}/mo retail</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>If purchased separately:</span>
                  <span>${bundleSavings.retailTotal.toFixed(2)}/mo</span>
                </div>
                <div className="flex justify-between text-sm font-medium">
                  <span>Bundle price:</span>
                  <span>${bundleSavings.bundlePrice.toFixed(2)}/mo</span>
                </div>
                <div className="flex justify-between text-sm text-green-600 font-medium">
                  <span>You save:</span>
                  <span>${bundleSavings.savings.toFixed(2)}/mo ({bundleSavings.savingsPercent}%)</span>
                </div>
              </div>

              {currentBundle.payment_method === 'credit_card_perk' && (
                <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2 text-sm text-blue-600">
                  <CreditCard className="w-4 h-4" />
                  <span>Paid via {currentBundle.payment_source}</span>
                  {currentBundle.credit_applied > 0 && (
                    <span className="text-gray-500">(${currentBundle.credit_applied.toFixed(2)} credit)</span>
                  )}
                </div>
              )}
            </div>

            {/* Action Selection */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">What would you like to do?</h3>
              <div className="space-y-2">
                
                {/* Switch Bundle Option */}
                {alternativeBundles.length > 0 && (
                  <button
                    onClick={() => setSelectedAction(selectedAction === 'switch' ? null : 'switch')}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                      selectedAction === 'switch' 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Switch to a different bundle</p>
                        <p className="text-sm text-gray-500">Choose from {alternativeBundles.length} other options</p>
                      </div>
                      <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${selectedAction === 'switch' ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                )}

                {/* Switch Bundle Details */}
                {selectedAction === 'switch' && (
                  <div className="ml-4 space-y-2 animate-in slide-in-from-top-2">
                    {alternativeBundles.map(bundle => (
                      <label
                        key={bundle.bundle_code}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedNewBundle === bundle.bundle_code
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="newBundle"
                          value={bundle.bundle_code}
                          checked={selectedNewBundle === bundle.bundle_code}
                          onChange={(e) => setSelectedNewBundle(e.target.value)}
                          className="text-purple-600 focus:ring-purple-500"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{bundle.bundle_name}</p>
                          <p className="text-sm text-gray-500">
                            {(bundle.serviceNames || []).join(' + ')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">${bundle.monthly_cost?.toFixed(2)}/mo</p>
                          <p className="text-xs text-green-600">Save ${bundle.savings?.toFixed(2)}</p>
                        </div>
                      </label>
                    ))}
                    
                    {/* Show conflict warning if switching will consolidate individual subscriptions */}
                    {switchConflicts.length > 0 && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">
                              Will consolidate {switchConflicts.length} individual subscription{switchConflicts.length > 1 ? 's' : ''}
                            </p>
                            <p className="text-sm text-blue-700 mt-1">
                              {switchConflicts.map(s => s.service_name).join(', ')} will be moved into the bundle
                              {switchConflicts.reduce((sum, s) => sum + (parseFloat(s.actual_cost) || parseFloat(s.monthly_cost) || 0), 0) > 0 && (
                                <span className="font-medium"> (saving ${switchConflicts.reduce((sum, s) => sum + (parseFloat(s.actual_cost) || parseFloat(s.monthly_cost) || 0), 0).toFixed(2)}/mo)</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Keep Individual Option */}
                <button
                  onClick={() => setSelectedAction(selectedAction === 'individual' ? null : 'individual')}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                    selectedAction === 'individual' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Keep as individual services</p>
                      <p className="text-sm text-gray-500">Choose which services to keep and how to pay</p>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${selectedAction === 'individual' ? 'rotate-90' : ''}`} />
                  </div>
                </button>

                {/* Individual Services Details */}
                {selectedAction === 'individual' && (
                  <div className="ml-4 space-y-3 animate-in slide-in-from-top-2">
                    {includedServices.map(service => (
                      <div key={service.name} className="p-3 rounded-lg border border-gray-200 bg-white">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={servicesToKeep[service.name] || false}
                            onChange={(e) => setServicesToKeep(prev => ({
                              ...prev,
                              [service.name]: e.target.checked
                            }))}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{service.name}</p>
                            <p className="text-sm text-gray-500">${service.retailPrice?.toFixed(2)}/mo</p>
                          </div>
                        </label>
                        
                        {servicesToKeep[service.name] && (
                          <div className="mt-3 pl-7">
                            <label className="text-sm text-gray-600 block mb-1">Payment method:</label>
                            <select
                              value={servicePaymentMethods[service.name] || 'paid'}
                              onChange={(e) => setServicePaymentMethods(prev => ({
                                ...prev,
                                [service.name]: e.target.value
                              }))}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              {getPaymentMethodsForService(service.name).map(method => (
                                <option key={method.value} value={method.value}>
                                  {method.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {!Object.values(servicesToKeep).some(v => v) && (
                      <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                        <AlertCircle className="w-4 h-4" />
                        <span>Select at least one service to keep, or choose "Cancel bundle entirely"</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Cancel Bundle Option */}
                <button
                  onClick={() => setSelectedAction(selectedAction === 'cancel' ? null : 'cancel')}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                    selectedAction === 'cancel' 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Cancel bundle entirely</p>
                      <p className="text-sm text-gray-500">Remove bundle and all included services</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => setShowConfirmPrompt(true)}
              disabled={
                !selectedAction ||
                (selectedAction === 'switch' && !selectedNewBundle) ||
                (selectedAction === 'individual' && !Object.values(servicesToKeep).some(v => v))
              }
              className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Prompt */}
      <CascadeDeletePrompt
        isOpen={showConfirmPrompt}
        onClose={() => setShowConfirmPrompt(false)}
        title={confirmContent.title}
        message={confirmContent.message}
        affectedItems={confirmContent.affectedItems}
        actions={confirmContent.actions}
        isProcessing={isProcessing}
      />
    </>
  );
};

export default BundleModal;